import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';

export function BudgetModal({ open, onClose, defaultYear }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ accountId: '', year: defaultYear, month: 1, budgetedAmount: '' });
  const [error, setError] = useState(null);

  const accounts = useQuery({
    queryKey: ['finance', 'accounts'],
    queryFn: () => financeApi.listAccounts().then((r) => r.items),
    enabled: open,
  });
  const budgetable = (accounts.data || []).filter((a) => a.type === 'EXPENSE' || a.type === 'REVENUE');

  useEffect(() => { if (open) { setForm({ accountId: '', year: defaultYear, month: 1, budgetedAmount: '' }); setError(null); } }, [open, defaultYear]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = useMutation({
    mutationFn: (body) => financeApi.upsertBudget(body),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['finance', 'budgets'] }); onClose(); },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (!form.accountId || form.budgetedAmount === '' || Number(form.budgetedAmount) < 0) { setError(t('bud.error.required')); return; }
    save.mutate({ accountId: form.accountId, year: Number(form.year), month: Number(form.month), budgetedAmount: Number(form.budgetedAmount) });
  }

  return (
    <Modal open={open} onClose={onClose} title={t('bud.modal.title')}
      footer={<><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="primary" onClick={submit} disabled={save.isPending}>{save.isPending ? `${t('common.save')}…` : t('common.save')}</Button></>}>
      <form onSubmit={submit} className="form-stack">
        <label className="field"><span className="field-label">{t('bud.col.account')}</span>
          <select className="input" value={form.accountId} onChange={set('accountId')} required>
            <option value="">-</option>
            {budgetable.map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name} ({a.type})</option>)}
          </select>
        </label>
        <div className="field-row">
          <label className="field" style={{ maxWidth: 120 }}><span className="field-label">{t('bud.field.year')}</span>
            <input className="input num" type="number" value={form.year} onChange={set('year')} />
          </label>
          <label className="field" style={{ maxWidth: 120 }}><span className="field-label">{t('bud.field.month')}</span>
            <select className="input" value={form.month} onChange={set('month')}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label className="field"><span className="field-label">{t('bud.field.amount')}</span>
            <input className="input num" type="number" min="0" step="0.01" value={form.budgetedAmount} onChange={set('budgetedAmount')} required />
          </label>
        </div>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
